import { useCallback, useMemo } from "react";

import { ICatalogue } from "../../../apis/useGetCatalogueData/types";
import { globalEvents } from "../../../common/helpers/GlobalEvents";
import { TOAST_EVENTS } from "../../../components/Toast/constants";
import {
	IOosItemDetail,
	createCatalogueMap,
	filterOutOosItems,
	getOosItemDetails,
	getOosItemNames,
	getOosItems,
} from "./helpers/oosDetectionHelpers";

export interface IOosDetectionLogic {
	catalogueMap: Map<string, ICatalogue>;
	oosItems: ICatalogue[];
	hasOosItems: boolean;
	oosItemNames: string[];
	oosItemDetails: IOosItemDetail[];
}

export interface ICartActions {
	UPDATE_CART: (cart: ICatalogue[]) => void;
}

/**
 * Pure logic for OOS detection - can be tested without React context
 */
export const useOosDetectionLogic = (
	cart: ICatalogue[],
	catalogueProducts: ICatalogue[]
): IOosDetectionLogic => {
	const catalogueMap = useMemo(() => createCatalogueMap(catalogueProducts), [catalogueProducts]);

	const oosItems = useMemo(() => getOosItems(cart, catalogueMap), [cart, catalogueMap]);

	const oosItemNames = useMemo(() => {
		if (oosItems.length > 0) {
			return getOosItemNames(oosItems);
		}

		return [];
	}, [oosItems]);

	const oosItemDetails = useMemo(() => {
		if (oosItems.length > 0) {
			return getOosItemDetails(oosItems, catalogueMap);
		}

		return [];
	}, [oosItems, catalogueMap]);

	return {
		catalogueMap,
		oosItems,
		hasOosItems: oosItems.length > 0,
		oosItemNames,
		oosItemDetails,
	};
};

/**
 * Create removeOosItems callback
 */
export const useRemoveOosItems = (
	cart: ICatalogue[],
	catalogueMap: Map<string, ICatalogue>,
	cartActions: ICartActions
) => {
	return useCallback(() => {
		const updatedCart = filterOutOosItems(cart, catalogueMap);

		cartActions.UPDATE_CART(updatedCart);

		setTimeout(() => {
			globalEvents.emit(TOAST_EVENTS.SHOW_REMOVE_ITEM_TOAST);
		}, 100);
	}, [cart, catalogueMap, cartActions]);
};
